import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import { CAP_DIRECTION_COLOR, capNoDirectionReason, parseAzimuthInput } from '../../utils/capMarkerUtils';
import { PopupButtonGrid, PopupSection, popupButtonSx, popupInlineButtonSx } from './devicePopupUi';

const fmtDeg = (v) => `${Math.round(v)}°`;

function formatSetAt(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/**
 * CAP antenna direction section for the map popup: shows the direction in
 * use and where it comes from, and (with write access) lets the user type
 * one, aim it by clicking the map, or reset back to the estimate.
 *
 * @param {object} props
 * @param {object|null} props.azimuth entry from capAzimuthEntryFromApi
 * @param {boolean} props.canEdit
 * @param {boolean} props.saving
 * @param {(deg:number) => void} props.onSave
 * @param {() => void} props.onReset
 * @param {() => void} props.onToggleAim starts aiming: the map hides this
 *   popup until the user clicks where the antenna points
 */
export default function CapAzimuthEditor({ azimuth, canEdit, saving, onSave, onReset, onToggleAim }) {
  const hasDirection = azimuth != null && azimuth.azimuthDeg != null;
  // The field starts at the direction in use (rounded); Set only enables
  // once the user types a different value, whatever its source.
  const initialValue = hasDirection ? Math.round(azimuth.azimuthDeg) : null;
  const [input, setInput] = useState(initialValue == null ? '' : String(initialValue));

  // Refill only when the direction in use changes, not on every list
  // refresh, so a value being typed is not overwritten.
  useEffect(() => {
    setInput(initialValue == null ? '' : String(initialValue));
  }, [initialValue]);

  const parsed = parseAzimuthInput(input);
  const isUser = hasDirection && azimuth.source === 'user';
  const unchanged = parsed != null && parsed === initialValue;
  const stop = (e) => e.stopPropagation();

  let summary;
  if (!hasDirection) {
    summary = capNoDirectionReason(azimuth);
  } else if (isUser) {
    const by = azimuth.userSetBy ? ` by ${azimuth.userSetBy}` : '';
    const at = formatSetAt(azimuth.userSetAt);
    summary = `${fmtDeg(azimuth.azimuthDeg)} · set${by}${at ? ` on ${at}` : ''}`;
  } else {
    summary = `${fmtDeg(azimuth.azimuthDeg)} · estimated${azimuth.lowConfidence ? ' (low confidence)' : ''}`;
  }

  return (
    <Box onClick={stop}>
      <PopupSection
        title="Antenna direction"
        icon={<ExploreIcon sx={{ fontSize: 15, color: CAP_DIRECTION_COLOR }} />}
        action={isUser ? (
          <Chip size="small" label="User set" sx={{ height: 18, fontSize: '0.65rem', color: 'white', bgcolor: CAP_DIRECTION_COLOR }} />
        ) : null}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{summary}</Typography>
        {isUser && azimuth.estimatedAzimuthDeg != null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Estimate from lamps: {fmtDeg(azimuth.estimatedAzimuthDeg)}{azimuth.estimateLowConfidence ? ' (low confidence)' : ''}
          </Typography>
        )}

        {canEdit && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                label="Degrees (0 = north)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onClick={stop}
                disabled={saving}
                error={input !== '' && parsed == null}
                inputProps={{ inputMode: 'decimal' }}
              />
              <Button
                variant="contained"
                size="small"
                disabled={saving || parsed == null || unchanged}
                onClick={(e) => {
                  stop(e);
                  onSave(parsed);
                }}
                sx={popupInlineButtonSx}
              >
                Set
              </Button>
            </Box>
            <PopupButtonGrid sx={{ mt: 0.75 }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                disabled={saving}
                onClick={(e) => {
                  stop(e);
                  onToggleAim();
                }}
                sx={popupButtonSx}
              >
                Aim on map
              </Button>
              {isUser && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  fullWidth
                  disabled={saving}
                  onClick={(e) => {
                    stop(e);
                    onReset();
                  }}
                  sx={popupButtonSx}
                >
                  Reset to estimate
                </Button>
              )}
            </PopupButtonGrid>
          </>
        )}
      </PopupSection>
    </Box>
  );
}
